/**
 * EmergencySOSButton
 *
 * Floating SOS button. Each user maintains their own Emergency Contacts list
 * (stored as emergencyContactIds on their profile — no admin required).
 * On SOS send, those family members receive a push notification immediately.
 */
import React, { useState, useRef } from 'react';
import {
  Autocomplete,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  IconButton,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Warning as SOSIcon,
  CheckCircle as ResolvedIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  MyLocation as GPSIcon,
  OpenInNew as MapIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { db } from '../firebase';
import { doc, getDoc, getDocs, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { sendEmergencySOS, captureGPS, reverseGeocode } from '../services/emergencySOSService';
import logger from '../utils/logger';

export default function EmergencySOSButton() {
  const { currentUser } = useAuth();
  const { profile, updateProfile } = useProfile();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sentAlertId, setSentAlertId] = useState(null);
  const [sentLocation, setSentLocation] = useState(null);
  /** External contacts we asked the backend to SMS (Twilio must be configured server-side). */
  const [sentSmsRecipients, setSentSmsRecipients] = useState([]);
  /** Per-contact delivery status written back by the Cloud Function. Null until received. */
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const deliveryUnsubRef = useRef(null);
  const [error, setError] = useState('');

  // App-user emergency contacts — resolved from profile.emergencyContactIds
  const [appContacts, setAppContacts] = useState([]);   // [{ uid, name, email }]
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [savingContacts, setSavingContacts] = useState(false);

  // Family member picker (for adding new emergency contacts)
  const [familyMembers, setFamilyMembers] = useState([]);  // available to add
  const [showPicker, setShowPicker] = useState(false);
  const [pickerUid, setPickerUid] = useState('');

  // External contacts (profile.emergencyContacts — non-app users, phone numbers)
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [extraContacts, setExtraContacts] = useState([]);

  // GPS preview
  const [gpsCapturing, setGpsCapturing] = useState(false);
  const [previewGPS, setPreviewGPS] = useState(null);

  const familyCode = profile?.familyCode || profile?.currentFamily;
  if (!familyCode || !currentUser) return null;

  const handleOpen = async () => {
    // Reset dialog state
    const saved = (profile?.emergencyContacts || []).map((c, i) => ({ ...c, _id: i }));
    setSelectedContacts(saved.map(c => c._id));
    setPreviewGPS(null);
    setMessage('');
    setSentAlertId(null);
    setSentSmsRecipients([]);
    setDeliveryStatus(null);
    setError('');
    setShowAddContact(false);
    setShowPicker(false);
    setPickerUid('');
    setNewContactName('');
    setNewContactPhone('');
    setExtraContacts([]);
    setDialogOpen(true);

    // Load app-user emergency contacts from profile.emergencyContactIds
    setLoadingContacts(true);
    try {
      const contactIds = (profile?.emergencyContactIds || []).filter(id => id !== currentUser.uid);

      // Resolve UIDs to names
      const resolved = [];
      if (contactIds.length > 0) {
        const userDocs = await Promise.all(contactIds.map(uid => getDoc(doc(db, 'users', uid))));
        userDocs.forEach(d => {
          if (!d.exists()) return;
          const data = d.data();
          const name = ((data.firstName || '') + ' ' + (data.lastName || '')).trim()
            || data.displayName || data.email || 'Unknown';
          resolved.push({ uid: d.id, name, email: data.email || '' });
        });
      }
      setAppContacts(resolved);

      // Load all family members (excluding self)
      const membersSnap = await getDocs(collection(db, 'families', familyCode, 'members'));
      const allMemberIds = membersSnap.docs
        .map(d => d.data().userId || d.id)
        .filter(id => id !== currentUser.uid);

      let resolvedMembers = [];
      if (allMemberIds.length > 0) {
        const memberDocs = await Promise.all(allMemberIds.map(uid => getDoc(doc(db, 'users', uid))));
        resolvedMembers = memberDocs
          .filter(d => d.exists())
          .map(d => {
            const data = d.data();
            const name = ((data.firstName || '') + ' ' + (data.lastName || '')).trim()
              || data.displayName || data.email || 'Unknown';
            return { uid: d.id, name, email: data.email || '' };
          });
      }

      if (contactIds.length === 0 && resolvedMembers.length > 0) {
        // No saved contacts yet — default to all family members so SOS works immediately
        setAppContacts(resolvedMembers);
        setFamilyMembers([]);
      } else {
        // Show only members not already in the saved contacts list
        setFamilyMembers(resolvedMembers.filter(m => !contactIds.includes(m.uid)));
      }
    } catch (err) {
      logger.error('Error loading emergency contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleClose = () => {
    if (deliveryUnsubRef.current) {
      deliveryUnsubRef.current();
      deliveryUnsubRef.current = null;
    }
    setDialogOpen(false);
    setTimeout(() => {
      setMessage('');
      setSentAlertId(null);
      setSentLocation(null);
      setDeliveryStatus(null);
      setError('');
      setPreviewGPS(null);
    }, 300);
  };

  // Persist updated emergencyContactIds to the user's profile
  const persistContactIds = async (newContacts) => {
    setSavingContacts(true);
    try {
      await updateProfile({ emergencyContactIds: newContacts.map(c => c.uid) });
    } catch (err) {
      logger.error('Error saving emergency contacts:', err);
      setError('Could not save contacts. Please try again.');
    } finally {
      setSavingContacts(false);
    }
  };

  const handleAddAppContact = async () => {
    if (!pickerUid) return;
    const member = familyMembers.find(m => m.uid === pickerUid);
    if (!member) return;

    const updated = [...appContacts, { uid: member.uid, name: member.name, email: '' }];
    setAppContacts(updated);
    setFamilyMembers(prev => prev.filter(m => m.uid !== pickerUid));
    setPickerUid('');
    setShowPicker(false);
    await persistContactIds(updated);
  };

  const handleRemoveAppContact = async (uid) => {
    const removed = appContacts.find(c => c.uid === uid);
    const updated = appContacts.filter(c => c.uid !== uid);
    setAppContacts(updated);
    if (removed) setFamilyMembers(prev => [...prev, { uid: removed.uid, name: removed.name }]);
    await persistContactIds(updated);
  };

  const handleCaptureGPS = async () => {
    setGpsCapturing(true);
    try {
      const loc = await captureGPS();
      if (loc) {
        const address = await reverseGeocode(loc.lat, loc.lng);
        setPreviewGPS({ ...loc, address });
      } else {
        setError('Could not get GPS location. Please enable location access.');
      }
    } catch (e) {
      logger.warn('GPS preview failed:', e.message);
    } finally {
      setGpsCapturing(false);
    }
  };

  const handleAddExternalContact = () => {
    if (!newContactName.trim() || !newContactPhone.trim()) return;
    const id = `new_${Date.now()}`;
    setExtraContacts(prev => [...prev, { _id: id, name: newContactName.trim(), phone: newContactPhone.trim(), relationship: '' }]);
    setSelectedContacts(prev => [...prev, id]);
    setNewContactName('');
    setNewContactPhone('');
    setShowAddContact(false);
  };

  const allExternalContacts = [
    ...(profile?.emergencyContacts || []).map((c, i) => ({ ...c, _id: i })),
    ...extraContacts,
  ];

  const toggleContact = (id) => {
    setSelectedContacts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const chosen = allExternalContacts.filter(c => selectedContacts.includes(c._id));
      const smsPayload = chosen.map(({ name, phone, email, relationship }) => ({ name, phone, email: email || '', relationship }));
      const { alertId, location } = await sendEmergencySOS({
        userId: currentUser.uid,
        displayName: profile?.displayName || currentUser.displayName || 'A family member',
        familyCode,
        message,
        emergencyContactIds: appContacts.map(c => c.uid),
        emergencyContacts: smsPayload,
      });
      setSentAlertId(alertId);
      setSentLocation(location);
      setSentSmsRecipients(smsPayload.map((c) => c.name || c.phone || 'Contact'));

      // Subscribe to the alert doc — the Cloud Function writes contactDeliveryStatus once done
      if (smsPayload.length > 0) {
        const alertRef = doc(db, 'families', familyCode, 'emergencyAlerts', alertId);
        deliveryUnsubRef.current = onSnapshot(alertRef, (snap) => {
          const d = snap.data();
          if (d?.contactDeliveryStatus) {
            setDeliveryStatus(d.contactDeliveryStatus);
            if (deliveryUnsubRef.current) {
              deliveryUnsubRef.current();
              deliveryUnsubRef.current = null;
            }
          }
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to send SOS alert');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Tooltip title="Send Emergency SOS" placement="left">
        <Fab
          color="error"
          size="large"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 16,
            zIndex: 1300,
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(211,47,47,0.45)',
          }}
          aria-label="Send Emergency SOS"
        >
          <SOSIcon />
        </Fab>
      </Tooltip>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        {!sentAlertId ? (
          <>
            <DialogTitle sx={{ color: 'error.main', fontWeight: 700, pb: 1 }}>
              🚨 Send Emergency SOS
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              {/* Custom message */}
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Message (optional)"
                placeholder="e.g. I've been in an accident on Route 1 near the mall"
                value={message}
                onChange={e => setMessage(e.target.value)}
                disabled={sending}
                sx={{ mb: 2 }}
              />

              {/* GPS location preview */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={gpsCapturing ? <CircularProgress size={14} /> : <GPSIcon />}
                    onClick={handleCaptureGPS}
                    disabled={gpsCapturing || sending}
                    color={previewGPS ? 'success' : 'primary'}
                  >
                    {gpsCapturing ? 'Getting location…' : previewGPS ? 'Location captured' : 'Capture my location now'}
                  </Button>
                  {previewGPS && (
                    <Typography
                      variant="caption"
                      component="a"
                      href={`https://maps.google.com/?q=${previewGPS.lat},${previewGPS.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'primary.main', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: 0.3 }}
                    >
                      <MapIcon sx={{ fontSize: '0.8rem' }} /> View
                    </Typography>
                  )}
                </Box>
                {previewGPS?.address && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', ml: 0.5 }}>
                    📍 {previewGPS.address}
                  </Typography>
                )}
                {!previewGPS && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    Location will be auto-captured when you send. Capture now to preview.
                  </Typography>
                )}
              </Box>

              {/* ── App-user Emergency Contacts ─────────────────────────────── */}
              <Divider sx={{ mb: 1.5 }}>
                <Chip label="Who gets alerted" size="small" color="error" />
              </Divider>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                These family members will receive an immediate app notification.
                You control this list — add or remove anyone.
              </Typography>

              {loadingContacts ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">Loading…</Typography>
                </Box>
              ) : (
                <>
                  {appContacts.length > 0 ? (
                    <List dense disablePadding sx={{ mb: 1 }}>
                      {appContacts.map(c => (
                        <ListItem key={c.uid} disableGutters sx={{ py: 0.25 }}>
                          <ListItemText
                            primary={<Typography variant="body2" fontWeight={600}>{c.name}</Typography>}
                            secondary={c.email
                              ? <Typography variant="caption" color="text.secondary">{c.email}</Typography>
                              : null}
                          />
                          <ListItemSecondaryAction>
                            <Chip label="Push" size="small" color="error" variant="outlined" sx={{ mr: 1 }} />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveAppContact(c.uid)}
                              disabled={savingContacts || sending}
                              aria-label={`Remove ${c.name}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      No family members found to alert.
                    </Typography>
                  )}

                  {/* Add family member picker */}
                  {showPicker ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel>Select family member</InputLabel>
                        <Select
                          value={pickerUid}
                          label="Select family member"
                          onChange={e => setPickerUid(e.target.value)}
                          disabled={savingContacts}
                        >
                          {familyMembers.length === 0 ? (
                            <MenuItem disabled value="">All family members already added</MenuItem>
                          ) : (
                            familyMembers.map(m => (
                              <MenuItem key={m.uid} value={m.uid}>{m.name}</MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleAddAppContact}
                        disabled={!pickerUid || savingContacts}
                      >
                        {savingContacts ? <CircularProgress size={14} /> : 'Add'}
                      </Button>
                      <Button size="small" onClick={() => { setShowPicker(false); setPickerUid(''); }}>
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setShowPicker(true)}
                      disabled={sending || familyMembers.length === 0}
                      sx={{ mb: 1 }}
                    >
                      Add family member
                    </Button>
                  )}
                </>
              )}

              {/* ── External contacts (non-app, phone only) ─────────────────── */}
              {allExternalContacts.length > 0 && (
                <>
                  <Divider sx={{ mb: 1, mt: 0.5 }}>
                    <Chip label="External contacts" size="small" />
                  </Divider>
                  <List dense disablePadding sx={{ mb: 1 }}>
                    {allExternalContacts.map(contact => (
                      <ListItem key={contact._id} disableGutters sx={{ py: 0.25 }}>
                        <Checkbox
                          checked={selectedContacts.includes(contact._id)}
                          onChange={() => toggleContact(contact._id)}
                          size="small"
                          disabled={sending}
                          sx={{ py: 0 }}
                        />
                        <ListItemText
                          primary={<Typography variant="body2">{contact.name}</Typography>}
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {contact.phone}{contact.relationship ? ` — ${contact.relationship}` : ''}
                            </Typography>
                          }
                        />
                        {String(contact._id).startsWith('new_') && (
                          <ListItemSecondaryAction>
                            <IconButton size="small" onClick={() => {
                              setExtraContacts(prev => prev.filter(c => c._id !== contact._id));
                              setSelectedContacts(prev => prev.filter(x => x !== contact._id));
                            }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {showAddContact ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 0.5, flexWrap: 'wrap' }}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    sx={{ flex: 2, minWidth: 140 }}
                    disabled={sending}
                    options={[
                      ...(profile?.emergencyContacts || []),
                      ...familyMembers.map(m => ({ name: m.name, phone: '' })),
                    ]}
                    getOptionLabel={o => (typeof o === 'string' ? o : o.name || '')}
                    filterOptions={(options, { inputValue }) => {
                      const lower = inputValue.toLowerCase();
                      return options.filter(o =>
                        (o.name || '').toLowerCase().includes(lower)
                      );
                    }}
                    inputValue={newContactName}
                    onInputChange={(_, value) => setNewContactName(value)}
                    onChange={(_, selected) => {
                      if (selected && typeof selected === 'object') {
                        setNewContactName(selected.name || '');
                        if (selected.phone) setNewContactPhone(selected.phone);
                      }
                    }}
                    renderOption={(props, o) => (
                      <li {...props} key={o.name + (o.phone || '')}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{o.name}</Typography>
                          {o.phone && (
                            <Typography variant="caption" color="text.secondary">{o.phone}</Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField {...params} label="Name" placeholder="Type to search or enter new" />
                    )}
                  />
                  <TextField size="small" label="Phone" value={newContactPhone}
                    onChange={e => setNewContactPhone(e.target.value)} sx={{ flex: 1, minWidth: 120 }} disabled={sending} />
                  <Button size="small" variant="contained" onClick={handleAddExternalContact}
                    disabled={!newContactName.trim() || !newContactPhone.trim()}>Add</Button>
                  <Button size="small" onClick={() => setShowAddContact(false)}>Cancel</Button>
                </Box>
              ) : (
                <Button size="small" startIcon={<AddIcon />}
                  onClick={() => setShowAddContact(true)} disabled={sending}>
                  Add external contact
                </Button>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleClose} disabled={sending}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleSend}
                disabled={sending || appContacts.length === 0}
                startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SOSIcon />}
                sx={{ fontWeight: 700 }}
              >
                {sending ? 'Sending…' : 'Send SOS Alert'}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                <ResolvedIcon />
                SOS Alert Sent
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your emergency contacts have been notified. Help is on the way.
              </Alert>
              {appContacts.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Push notification sent to:
                  </Typography>
                  {appContacts.map(c => (
                    <Typography key={c.uid} variant="body2">{c.name}</Typography>
                  ))}
                </Box>
              )}
              {sentSmsRecipients.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    External contacts:
                  </Typography>
                  {deliveryStatus ? (
                    deliveryStatus.map((s, i) => {
                      const smsSent = s.smsStatus === 'sent';
                      const emailSent = s.emailStatus === 'sent';
                      const noSms = s.smsStatus === 'no_sms_config' || s.smsStatus === 'failed';
                      return (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ minWidth: 100 }}>{s.name}</Typography>
                          {smsSent && (
                            <Chip label="SMS sent" size="small" color="success" variant="outlined" />
                          )}
                          {noSms && !emailSent && (
                            <Chip label="SMS blocked" size="small" color="warning" variant="outlined" />
                          )}
                          {emailSent && (
                            <Chip label="Email sent" size="small" color="info" variant="outlined" />
                          )}
                          {noSms && !emailSent && !s.email && (
                            <Typography variant="caption" color="text.secondary">
                              (add email in Profile for fallback)
                            </Typography>
                          )}
                          {s.smsStatus === 'no_phone' && (
                            <Chip label="No phone" size="small" variant="outlined" />
                          )}
                        </Box>
                      );
                    })
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <CircularProgress size={12} />
                      <Typography variant="caption" color="text.secondary">
                        Notifying {sentSmsRecipients.join(', ')}…
                      </Typography>
                    </Box>
                  )}
                  {deliveryStatus && deliveryStatus.some(s => s.smsStatus === 'no_sms_config' || s.smsStatus === 'failed') &&
                    !deliveryStatus.some(s => s.emailStatus === 'sent') && (
                    <Alert severity="warning" sx={{ mt: 1 }} icon={false}>
                      SMS is temporarily unavailable (carrier registration pending). Add email addresses to your emergency contacts in Profile to enable email fallback.
                    </Alert>
                  )}
                </Box>
              )}
              {sentLocation && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Your location was shared:
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MapIcon />}
                    component="a"
                    href={`https://maps.google.com/?q=${sentLocation.lat},${sentLocation.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Google Maps
                  </Button>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="contained" onClick={handleClose}>Done</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
